import type { PopoverRootProps } from "@v1/components/designSystem/popover/Popover";
import type { Swatch, SwatchType } from "replo-runtime/shared/types";

import * as React from "react";

import DeprecatedLabeledControl from "@v1/components/common/designSystem/DeprecatedLabeledControl";
import { Group, GroupHeader } from "@v1/components/common/designSystem/Group";
import InputComponent from "@v1/components/common/designSystem/Input";
import InlineAlert from "@v1/components/designSystem/alert/InlineAlert";
import DeprecatedButton from "@v1/components/designSystem/button/Button";
import Popover from "@v1/components/designSystem/popover/Popover";
import Selectable from "@v1/components/designSystem/Selectable";
import { ProductSelectionCombobox } from "@v1/components/editor/page/ProductSelectionCombobox";
import {
  getSwatchOptionsRows,
  getSwatchVariantsRows,
  SwatchOptionRow,
  SwatchVariantRow,
} from "@v1/components/editor/page/SwatchRow";
import { useSpecificStoreProducts } from "@v1/hooks/useStoreProducts";
import { swatchRouter, swatchUtils } from "@v1/trpc/routes/swatch";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { useAreModalsOpen, useModal } from "@v2/store/modalStore";
import { useOverridableState } from "replo-runtime/shared/hooks/useOverridableState";
import { useDebouncedCallback } from "replo-utils/react/use-debounced-callback";

type SwatchPopoverProps = {
  swatch: Swatch;
  isNewSwatch: boolean;
  isReadOnly: boolean;
  isOpen?: PopoverRootProps["isOpen"];
  onOpenChange?: PopoverRootProps["onOpenChange"];
};

const SwatchPopover: React.FC<SwatchPopoverProps> = ({
  swatch,
  isNewSwatch,
  isReadOnly,
  isOpen,
  onOpenChange,
}) => {
  const projectId = useCurrentProjectId()!;
  const areModalsOpen = useAreModalsOpen();
  const modal = useModal();
  const { mutate: mutateSwatch } = swatchRouter.update.useMutation({
    onMutate: async ({ swatch: updatedSwatch }) => {
      await swatchUtils.list.cancel();
      const previousSwatches = swatchUtils.list.getData();
      swatchUtils.list.setData({ projectId }, (old: Swatch[] | undefined) => {
        const index = old?.findIndex((s) => s.id === updatedSwatch.id);
        if (index === -1 || index === undefined) {
          return [...(old || []), updatedSwatch];
        }
        return old?.map((swatch) => {
          return swatch.id === updatedSwatch.id
            ? { ...swatch, ...updatedSwatch }
            : swatch;
        });
      });

      return { previousSwatches };
    },
    onError: (_err, _updatedSwatch, context) => {
      swatchUtils.list.setData({ projectId }, context?.previousSwatches);
    },
    onSettled: () => {
      void swatchUtils.list.invalidate();
    },
  });
  const { products } = useSpecificStoreProducts(swatch.data.productIds ?? [], {
    forceSkip: !swatch.data.productIds,
  });

  const numOptions = React.useMemo(
    () => products.reduce((acc, product) => acc + product.options.length, 0),
    [products],
  );

  const onChangeName = React.useCallback(
    (value: string) => {
      mutateSwatch({
        projectId,
        swatch: { ...swatch, name: value },
      });
    },
    [swatch, mutateSwatch, projectId],
  );

  const handleDeletion = () => {
    modal.openModal({
      type: "deleteSwatchConfirmationModal",
      props: {
        swatchId: swatch.id,
        swatchName: swatch.name,
        onDelete: () => onOpenChange?.(false),
      },
    });
  };

  const [name, setName] = useOverridableState(
    swatch.name,
    useDebouncedCallback(onChangeName, 300),
  );

  return (
    <Popover.Root isOpen={isOpen} onOpenChange={onOpenChange}>
      <Popover.Content
        title={getTitle(isReadOnly, isNewSwatch)}
        shouldPreventDefaultOnInteractOutside={areModalsOpen}
        className="w-[348px]"
        side="left"
        sideOffset={-170}
      >
        <div className="flex flex-col mx-1 max-h-[275px] gap-2 overflow-y-auto">
          {isReadOnly && (
            <InlineAlert variant="warning" multiline>
              Editing is disabled for placeholder swatches. Click the + icon to
              add a swatch.
            </InlineAlert>
          )}
          <div className="flex gap-2">
            <div className="flex-1">
              <DeprecatedLabeledControl label="Name" size="sm" id="swatch-name">
                <InputComponent
                  size="sm"
                  autoFocus
                  value={name}
                  onChange={(e) => {
                    const { value } = e.target;
                    setName(value);
                  }}
                  isDisabled={isReadOnly}
                  id="swatch-name"
                />
              </DeprecatedLabeledControl>
            </div>
            <div className="flex-1">
              <DeprecatedLabeledControl label="Swatch Type" size="sm">
                <Selectable
                  placeholder=""
                  options={[
                    { label: "By Option", value: "option" },
                    { label: "By Variant", value: "variant" },
                  ]}
                  defaultValue={swatch.data?.type}
                  onSelect={(newValue) => {
                    if (projectId && newValue) {
                      mutateSwatch({
                        projectId,
                        swatch: {
                          ...swatch,
                          data: {
                            ...swatch.data,
                            type: newValue as SwatchType,
                          },
                        },
                      });
                    }
                  }}
                  isDisabled={isReadOnly}
                />
              </DeprecatedLabeledControl>
            </div>
          </div>
          <div className="w-full">
            {!isReadOnly && (
              <ProductSelectionCombobox
                key={swatch.id}
                onChange={(newValue) => {
                  const newProductIds = newValue?.map(
                    (product) => product.productId,
                  );
                  const newData =
                    newProductIds && newProductIds.length > 0
                      ? {
                          ...swatch.data,
                          productIds: newValue!.map((p) => Number(p.productId)),
                        }
                      : Object.assign({}, swatch.data, {
                          productIds: undefined,
                        });
                  if (projectId) {
                    mutateSwatch({
                      projectId,
                      swatch: { ...swatch, data: newData },
                    });
                  }
                }}
                value={(swatch.data?.productIds ?? []).map((id) => ({
                  productId: id,
                }))}
                isMultiselect
                sideOffset={18}
              />
            )}
          </div>

          {swatch.data?.type === "option" && numOptions > 0 && (
            <Group name="Options" className="flex flex-col">
              <ul className="flex w-full flex-col gap-2 text-xs pt-1">
                {products.map((product) => (
                  <Group
                    key={product.id}
                    name={product.title}
                    isCollapsible
                    isDefaultOpen
                    header={
                      <GroupHeader className="w-[320px] overflow-hidden" />
                    }
                  >
                    {product.options.length > 0 && (
                      <ul className="flex w-full flex-col gap-2 text-xs">
                        {getSwatchOptionsRows(product, swatch).map((option) => (
                          <li
                            key={option.id}
                            className="grid grid-cols-2 items-center gap-1"
                          >
                            <SwatchOptionRow
                              option={option}
                              swatch={swatch}
                              isReadOnly={isReadOnly}
                            />
                          </li>
                        ))}
                      </ul>
                    )}
                  </Group>
                ))}
              </ul>
            </Group>
          )}

          {swatch.data?.type === "variant" && (
            <Group name="Variants" className="flex flex-col">
              <div className="mt-1 flex flex-col gap-3">
                {products.map((product) => (
                  <Group
                    key={product.id}
                    name={product.title}
                    isCollapsible
                    isDefaultOpen
                    header={
                      <GroupHeader className="w-[320px] overflow-hidden" />
                    }
                  >
                    {product.variants.length > 0 && (
                      <ul className="flex w-full flex-col gap-2 text-xs">
                        {getSwatchVariantsRows(product, swatch).map(
                          (variant) => (
                            <li
                              key={variant.id}
                              className="grid grid-cols-2 items-center gap-1"
                            >
                              <SwatchVariantRow
                                variant={variant}
                                swatch={swatch}
                                isReadOnly={isReadOnly}
                              />
                            </li>
                          ),
                        )}
                      </ul>
                    )}
                  </Group>
                ))}
              </div>
            </Group>
          )}
          {!isReadOnly && (
            <DeprecatedButton
              layoutClassName="w-full"
              onClick={handleDeletion}
              variant="danger"
            >
              Delete Swatch
            </DeprecatedButton>
          )}
        </div>
      </Popover.Content>
      <Popover.Anchor className="absolute left-[-180px] top-0" />
    </Popover.Root>
  );
};

export default SwatchPopover;

function getTitle(isReadOnly: boolean, isNewSwatch: boolean) {
  if (isNewSwatch) {
    return "New Swatch";
  }
  if (isReadOnly) {
    return "View Swatch";
  }
  return "Edit Swatch";
}
